import { forwardRef } from 'react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { format } from 'date-fns';
import { Box } from '@mantine/core';

import { getCdnUrl } from '~/domains/assets/utils/getCdnUrl';
import { Text } from '~/domains/design-system/Text';


import type { MissionCardProps } from '../../types/missions.types';

import { useStyles } from './MissionCard.styles';
import { useMissionCard } from './useMissionCard';


export const MissionCard = forwardRef<HTMLDivElement, MissionCardProps>(
  ({
    missionAssignment,
    hasBorder = false,
    onClick,
    isCompact = false,
  }, ref) => {
  const { t } = useTranslation('promotions');
  const { expirationChipLabel, progressWidth } = useMissionCard({ missionAssignment });
  const { classes, cx } = useStyles({ progressWidth });

  if (!missionAssignment || !missionAssignment.mission) return null;
  const { status, progress, completedAt } = missionAssignment;
  const { slug, title, description, rewardValue, iconUrl } = missionAssignment.mission;

  const badgeIcon = iconUrl
    ? <Image
      src={iconUrl}
      alt={title}
      width={34}
      height={34}
      className={classes.badgeIcon}
    /> : null;

  return (
    <Box
      className={cx(classes.container, {
        [classes.hasBorder]: hasBorder,
        [classes.expiring]: expirationChipLabel,
        'compact': isCompact,
      })}
      key={slug}
      ref={ref}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {expirationChipLabel && (
        <div className={classes.expirationChip}>
          <Text variant="subhead-x-small" display="block">
            {expirationChipLabel}
          </Text>
        </div>
      )}
      <div className={classes.badge}>
        {badgeIcon || (
          <Text
            variant="display-headline-condensed-xl"
            className={classes.rewardValue}
          >
            {rewardValue}
          </Text>
        )}
        <Image
          className={classes.badgeBgr}
          src={getCdnUrl('/images/loyalty/MissionBadge-small.png')}
          alt="Challenge badge"
          width={54}
          height={54}
        />
      </div>
      <div className={classes.content}>
        <Text variant="subhead-small" className={classes.title}>{title}</Text>
        <Text variant="subhead-x-small" className={classes.description}>{description}</Text>
        {status === 'started' && progress && (
          <div className={classes.progressContainer}>
            <div className={classes.progressTrack}>
              <div className={classes.progressBarFill} />
            </div>
            <Text variant="subhead-x-small" className={classes.progressCaption}>{progress.current} / {progress.required}</Text>
          </div>
        )}
        {status === 'completed' && completedAt && (
          <Text variant="subhead-x-small" className={classes.completedAt}>
            {t('promoCard.completion.completedAt')} {format(new Date(completedAt), 'MMM d, yyyy')}
          </Text>
        )}
      </div>
    </Box>
  );
});

MissionCard.displayName = 'MissionCard';
